import {
  ApolloLink,
  FetchResult,
  fromPromise,
  Observable,
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'

/**
 * Apollo link that intercepts 401 network errors, drives a forced refresh,
 * and retries the operation exactly once. Sits ahead of the HTTP link so it
 * sees the raw transport error.
 *
 * On a 401, calls `forceRefreshAccessToken`. The retry re-enters `authLink`,
 * which reads the now-fresh access token from session — no need to rebuild
 * the Authorization header here. The `__authRetried` context flag guarantees
 * one-shot: if the retry also 401s, the original error propagates so the
 * caller's existing error path (initAuthUser → LOGOUT) runs.
 *
 * If the refresh itself fails (revoked refresh token, network blip, server
 * 5xx) we re-emit the original networkError so callers see the same failure
 * shape they would have without this link in the chain.
 */
export function createRefreshOn401Link(
  forceRefreshAccessToken: () => Promise<string>,
): ApolloLink {
  return onError(({ networkError, operation, forward }) => {
    const status =
      networkError && 'statusCode' in networkError
        ? networkError.statusCode
        : undefined
    if (status !== 401) return

    if (operation.getContext().__authRetried) return

    return fromPromise(
      forceRefreshAccessToken().catch(() => null as string | null),
    ).flatMap(refreshedToken => {
      if (!refreshedToken) {
        return new Observable<FetchResult>(observer => {
          observer.error(networkError)
        })
      }
      operation.setContext({ __authRetried: true })
      return forward(operation)
    })
  })
}
